import { useEffect, useRef, useState, useCallback } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import api from "@/lib/api";
import { fdateShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  ArrowLeft, Type, ImagePlus, QrCode, Shapes, Sparkles, Trash2, Copy, Download, Save,
  ChevronUp, ChevronDown, Plus, Eye, Pencil, Bold, Italic, AlignLeft, AlignCenter, AlignRight, DownloadCloud,
} from "lucide-react";
import StationeryCanvas from "@/components/stationery/StationeryCanvas";
import DecorSvg, { DECOR_MOTIFS } from "@/components/stationery/DecorSvg";
import { FONTS, newTextLayer, newDecorLayer, newShapeLayer, newQrLayer, newImageLayer, uid } from "@/lib/stationery";
import { GAZ_W, GAZ_H, BLOCKS, blockPage, emptyPage } from "@/lib/gazette";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function GazetteEditor({ wid, gazette, startPreview, onBack }) {
  const [pages, setPages] = useState(gazette.pages?.length ? gazette.pages : [emptyPage(gazette.template_id)]);
  const [title, setTitle] = useState(gazette.title);
  const [status, setStatus] = useState(gazette.status || "draft");
  const [cur, setCur] = useState(0);
  const [selId, setSelId] = useState(null);
  const [scale, setScale] = useState(0.7);
  const [preview, setPreview] = useState(!!startPreview);
  const [busy, setBusy] = useState(false);
  const [saveState, setSaveState] = useState("saved");
  const stageRef = useRef(null);
  const fileRef = useRef(null);
  const exportRefs = useRef({});
  const saveTimer = useRef(null);

  const page = pages[cur] || pages[0];
  const sel = page?.layers.find((l) => l.id === selId);

  useEffect(() => {
    const fit = () => setScale(Math.min(0.8, Math.max(0.4, Math.min(window.innerWidth - 640, 460) / GAZ_W)));
    fit(); window.addEventListener("resize", fit); return () => window.removeEventListener("resize", fit);
  }, []);

  const persist = useCallback((p, t, st) => {
    setSaveState("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try { await api.put(`/weddings/${wid}/gazettes/${gazette.id}`, { pages: p, title: t, status: st }); setSaveState("saved"); }
      catch { setSaveState("error"); }
    }, 700);
  }, [wid, gazette.id]);

  const commitPages = (next, t = title, st = status) => { setPages(next); persist(next, t, st); };
  const updPage = (patch) => commitPages(pages.map((p, i) => (i === cur ? { ...p, ...patch } : p)));
  const updLayer = (id, patch) => updPage({ layers: page.layers.map((l) => (l.id === id ? { ...l, ...patch } : l)) });
  const addLayer = (l) => { updPage({ layers: [...page.layers, l] }); setSelId(l.id); };
  const removeLayer = (id) => { updPage({ layers: page.layers.filter((l) => l.id !== id) }); setSelId(null); };
  const dupLayer = (id) => { const l = page.layers.find((x) => x.id === id); if (l) addLayer({ ...l, id: uid(), x: l.x + 16, y: l.y + 16 }); };
  const moveLayer = (id, dir) => {
    const i = page.layers.findIndex((l) => l.id === id); const j = dir === "up" ? i + 1 : i - 1;
    if (i < 0 || j < 0 || j >= page.layers.length) return;
    const arr = [...page.layers];[arr[i], arr[j]] = [arr[j], arr[i]]; updPage({ layers: arr });
  };

  // page ops
  const addPage = (p) => { const next = [...pages]; next.splice(cur + 1, 0, p); commitPages(next); setCur(cur + 1); setSelId(null); };
  const dupPage = () => addPage({ id: uid(), bg: page.bg, layers: page.layers.map((l) => ({ ...l, id: uid() })) });
  const delPage = () => { if (pages.length <= 1) { toast.error("Au moins une page est requise"); return; } const next = pages.filter((_, i) => i !== cur); commitPages(next); setCur(Math.max(0, cur - 1)); setSelId(null); };
  const movePage = (dir) => { const j = dir === "up" ? cur - 1 : cur + 1; if (j < 0 || j >= pages.length) return; const next = [...pages];[next[cur], next[j]] = [next[j], next[cur]]; commitPages(next); setCur(j); };

  const uploadPhoto = async (e, targetId) => {
    const f = e.target.files?.[0]; if (!f) return; setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/weddings/${wid}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${BACKEND}${data.url}`;
      if (targetId) updLayer(targetId, { src: url }); else addLayer(newImageLayer(url));
    } catch { toast.error("Échec du téléversement"); } finally { setBusy(false); e.target.value = ""; }
  };

  const importInfo = async () => {
    try {
      const { data } = await api.get(`/weddings/${wid}/site`);
      const sched = data.site?.config?.sections?.schedule?.items || [];
      const w = data.wedding;
      const t = { bg: page.bg };
      const layers = [
        { id: uid(), type: "text", text: "LE PROGRAMME", x: 40, y: 60, w: 480, size: 14, spacing: 6, font: "montserrat", color: "#8a7550", align: "center", weight: 400, italic: false, lineHeight: 1.3, rotation: 0 },
        { id: uid(), type: "text", text: `${w.names} · ${fdateShort(w.wedding_date)}`, x: 40, y: 100, w: 480, size: 22, font: "playfair", color: "#222", align: "center", weight: 400, italic: false, spacing: 0, lineHeight: 1.3, rotation: 0 },
        ...(sched.length ? sched : [{ time: "15h00", title: "Cérémonie" }, { time: "19h00", title: "Dîner" }]).map((it, i) => (
          { id: uid(), type: "text", text: `${it.time || ""}  ·  ${it.title || ""}`, x: 60, y: 190 + i * 56, w: 440, size: 20, font: "playfair", color: "#333", align: "center", weight: 400, italic: false, spacing: 0, lineHeight: 1.3, rotation: 0 })),
      ];
      addPage({ id: uid(), bg: t.bg, layers });
      toast.success("Programme importé depuis votre mariage");
    } catch { toast.error("Import impossible"); }
  };

  const saveNow = async () => { clearTimeout(saveTimer.current); await api.put(`/weddings/${wid}/gazettes/${gazette.id}`, { pages, title, status }); setSaveState("saved"); toast.success("Enregistré"); };
  const setDone = (v) => { const st = v ? "done" : "draft"; setStatus(st); persist(pages, title, st); };

  const exportPDF = async () => {
    setBusy(true);
    try {
      await new Promise((r) => setTimeout(r, 60));
      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      for (let i = 0; i < pages.length; i++) {
        const el = exportRefs.current[pages[i].id]; if (!el) continue;
        const c = await html2canvas(el, { scale: 2.75, useCORS: true, backgroundColor: pages[i].bg, logging: false });
        const img = c.toDataURL("image/jpeg", 0.96);
        if (i > 0) pdf.addPage();
        pdf.addImage(img, "JPEG", 0, 0, 210, 297);
      }
      pdf.save(`${title || "gazette"}.pdf`); toast.success("PDF exporté");
    } catch { toast.error("Échec de l'export PDF"); } finally { setBusy(false); }
  };
  const exportPNG = async () => {
    setBusy(true);
    try {
      for (let i = 0; i < pages.length; i++) {
        const el = exportRefs.current[pages[i].id]; if (!el) continue;
        const c = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: pages[i].bg, logging: false });
        const a = document.createElement("a"); a.download = `${title || "gazette"}-p${i + 1}.png`; a.href = c.toDataURL("image/png"); a.click();
        await new Promise((r) => setTimeout(r, 200));
      }
      toast.success("Pages exportées en PNG");
    } catch { toast.error("Échec de l'export"); } finally { setBusy(false); }
  };

  // ---------- PREVIEW MODE ----------
  if (preview) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-5">
          <Button variant="outline" size="sm" onClick={() => (startPreview ? onBack() : setPreview(false))} data-testid="preview-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> {startPreview ? "Retour" : "Éditer"}</Button>
          <div className="font-serif text-lg">{title}</div>
          <div className="flex-1" />
          <Button size="sm" onClick={exportPDF} disabled={busy} className="rounded-full" data-testid="preview-export-pdf"><Download className="w-4 h-4 mr-1" /> PDF</Button>
        </div>
        <div className="flex flex-col items-center gap-6" data-testid="gazette-preview">
          {pages.map((p) => (
            <StationeryCanvas key={p.id} canvas={{ w: GAZ_W, h: GAZ_H, bg: p.bg, layers: p.layers }} scale={Math.min(0.62, (Math.min(window.innerWidth - 40, 620)) / GAZ_W)} />
          ))}
        </div>
        <ExportStack pages={pages} exportRefs={exportRefs} />
      </div>
    );
  }

  return (
    <div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e, fileRef.current?.dataset.target)} />
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="gaz-back"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
        <Input value={title} onChange={(e) => { setTitle(e.target.value); persist(pages, e.target.value, status); }} className="h-9 w-48" data-testid="gaz-title" />
        <span className="text-xs text-stone-400" data-testid="save-state">{saveState === "saving" ? "Sauvegarde…" : saveState === "error" ? "Erreur de sauvegarde" : "Sauvegardé"}</span>
        <div className="flex-1" />
        <label className="flex items-center gap-1.5 text-xs mr-1"><input type="checkbox" checked={status === "done"} onChange={(e) => setDone(e.target.checked)} data-testid="gaz-done" /> Terminée</label>
        <Button variant="outline" size="sm" onClick={importInfo} data-testid="gaz-import"><DownloadCloud className="w-4 h-4 mr-1" /> Importer les infos</Button>
        <Button variant="outline" size="sm" onClick={() => setPreview(true)} data-testid="gaz-preview"><Eye className="w-4 h-4 mr-1" /> Aperçu</Button>
        <Button variant="outline" size="sm" onClick={saveNow} data-testid="gaz-save"><Save className="w-4 h-4 mr-1" /> Enregistrer</Button>
        <Button variant="outline" size="sm" onClick={exportPNG} disabled={busy} data-testid="gaz-export-png"><Download className="w-4 h-4 mr-1" /> PNG</Button>
        <Button size="sm" onClick={exportPDF} disabled={busy} className="rounded-full" data-testid="gaz-export-pdf"><Download className="w-4 h-4 mr-1" /> PDF</Button>
      </div>

      <div className="grid lg:grid-cols-[120px_220px_1fr_260px] gap-4 items-start">
        {/* Pages */}
        <div className="space-y-2">
          <div className="eyebrow mb-1">Pages</div>
          {pages.map((p, i) => (
            <button key={p.id} onClick={() => { setCur(i); setSelId(null); }} data-testid={`gaz-page-${i}`}
              className={`w-full rounded-md overflow-hidden border p-1 relative ${i === cur ? "ring-2 ring-champagne" : ""}`}>
              <StationeryCanvas canvas={{ w: GAZ_W, h: GAZ_H, bg: p.bg, layers: p.layers }} scale={100 / GAZ_W} />
              <span className="absolute top-1 left-1 text-[10px] bg-white/90 rounded px-1">{i + 1}</span>
            </button>
          ))}
          <div className="flex gap-1">
            <button onClick={() => addPage(emptyPage(gazette.template_id))} title="Page vierge" data-testid="gaz-add-page" className="flex-1 h-8 rounded border flex items-center justify-center hover:bg-accent"><Plus className="w-4 h-4" /></button>
            <button onClick={dupPage} title="Dupliquer" className="flex-1 h-8 rounded border flex items-center justify-center hover:bg-accent"><Copy className="w-3.5 h-3.5" /></button>
            <button onClick={() => movePage("up")} title="Monter" className="h-8 w-8 rounded border flex items-center justify-center hover:bg-accent"><ChevronUp className="w-3.5 h-3.5" /></button>
            <button onClick={() => movePage("down")} title="Descendre" className="h-8 w-8 rounded border flex items-center justify-center hover:bg-accent"><ChevronDown className="w-3.5 h-3.5" /></button>
            <button onClick={delPage} title="Supprimer" className="h-8 w-8 rounded border flex items-center justify-center hover:bg-accent hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>

        {/* Tools */}
        <div className="card-lux p-4 space-y-4">
          <div>
            <div className="eyebrow mb-2">Ajouter</div>
            <div className="grid grid-cols-2 gap-2">
              <Tool icon={Type} label="Texte" testid="gz-add-text" onClick={() => addLayer(newTextLayer())} />
              <Tool icon={ImagePlus} label="Photo" testid="gz-add-photo" onClick={() => { fileRef.current.dataset.target = ""; fileRef.current.click(); }} />
              <Tool icon={QrCode} label="QR" testid="gz-add-qr" onClick={() => addLayer(newQrLayer(`${window.location.origin}/w/`))} />
              <Tool icon={Shapes} label="Ligne" onClick={() => addLayer(newShapeLayer("line"))} />
              <Tool icon={Shapes} label="Rect" onClick={() => addLayer(newShapeLayer("rect"))} />
              <Tool icon={Shapes} label="Cercle" onClick={() => addLayer(newShapeLayer("circle"))} />
            </div>
          </div>
          <div>
            <div className="eyebrow mb-2">Blocs de page</div>
            <div className="grid grid-cols-2 gap-1.5">
              {BLOCKS.map((b) => (
                <button key={b.key} onClick={() => addPage(blockPage(b.key, gazette.template_id, {}))} data-testid={`gz-block-${b.key}`}
                  className="text-[11px] px-2 py-1.5 rounded border hover:bg-accent text-left">{b.label}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="eyebrow mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Ornements</div>
            <div className="grid grid-cols-3 gap-1.5">
              {DECOR_MOTIFS.map((m) => (
                <button key={m} onClick={() => addLayer(newDecorLayer(m))} className="aspect-square rounded border p-1.5 hover:bg-accent" title={m}><DecorSvg motif={m} color="#8A7A5C" /></button>
              ))}
            </div>
          </div>
          <div><Label className="text-xs">Fond de page</Label>
            <input type="color" value={/^#/.test(page.bg) ? page.bg : "#ffffff"} onChange={(e) => updPage({ bg: e.target.value })} className="mt-1 w-full h-9 rounded border cursor-pointer" data-testid="gz-page-bg" /></div>
        </div>

        {/* Canvas */}
        <div className="flex justify-center py-2">
          <StationeryCanvas canvas={{ w: GAZ_W, h: GAZ_H, bg: page.bg, layers: page.layers }} scale={scale}
            interactive selectedId={selId} onSelect={setSelId} onChange={updLayer} stageRef={stageRef} />
        </div>

        {/* Props */}
        <div className="card-lux p-4 space-y-3 min-h-[200px]" data-testid="gz-props">
          {!sel ? <div className="text-sm text-stone-400 text-center py-8">Sélectionnez un élément pour le modifier.</div> : (
            <>
              <div className="flex items-center justify-between">
                <div className="eyebrow">{({ text: "Texte", image: "Photo", decor: "Ornement", shape: "Forme", qr: "QR" }[sel.type])}</div>
                <div className="flex gap-1">
                  <Ico icon={ChevronUp} onClick={() => moveLayer(sel.id, "up")} />
                  <Ico icon={ChevronDown} onClick={() => moveLayer(sel.id, "down")} />
                  <Ico icon={Copy} onClick={() => dupLayer(sel.id)} />
                  <Ico icon={Trash2} onClick={() => removeLayer(sel.id)} danger testid="gz-del-layer" />
                </div>
              </div>
              {sel.type === "text" && <TextProps l={sel} upd={(p) => updLayer(sel.id, p)} />}
              {sel.type === "image" && (
                <div className="space-y-3">
                  <Button variant="outline" size="sm" className="w-full" onClick={() => { fileRef.current.dataset.target = sel.id; fileRef.current.click(); }}><ImagePlus className="w-4 h-4 mr-1" /> Remplacer</Button>
                  <div><Label className="text-xs">Arrondi · {sel.radius}px</Label><Slider value={[sel.radius]} min={0} max={120} step={1} onValueChange={([v]) => updLayer(sel.id, { radius: v })} className="mt-2" /></div>
                </div>
              )}
              {sel.type === "decor" && (
                <div className="space-y-3">
                  <select value={sel.motif} onChange={(e) => updLayer(sel.id, { motif: e.target.value })} className="w-full h-9 rounded border px-2 text-sm bg-white">
                    {DECOR_MOTIFS.map((m) => <option key={m} value={m}>{m}</option>)}</select>
                  <Color label="Couleur" value={sel.color} onChange={(c) => updLayer(sel.id, { color: c })} />
                </div>
              )}
              {sel.type === "shape" && <Color label="Couleur" value={sel.fill} onChange={(c) => updLayer(sel.id, { fill: c, stroke: c })} />}
              {sel.type === "qr" && (
                <div className="space-y-3">
                  <Input value={sel.value} onChange={(e) => updLayer(sel.id, { value: e.target.value })} className="h-9 text-xs" data-testid="gz-qr-value" />
                  <Color label="Couleur" value={sel.color} onChange={(c) => updLayer(sel.id, { color: c })} />
                </div>
              )}
              <div><Label className="text-xs">Rotation · {sel.rotation || 0}°</Label><Slider value={[sel.rotation || 0]} min={-180} max={180} step={1} onValueChange={([v]) => updLayer(sel.id, { rotation: v })} className="mt-2" /></div>
            </>
          )}
        </div>
      </div>
      <ExportStack pages={pages} exportRefs={exportRefs} />
    </div>
  );
}

function ExportStack({ pages, exportRefs }) {
  return (
    <div style={{ position: "fixed", left: -99999, top: 0 }} aria-hidden>
      {pages.map((p) => (
        <StationeryCanvas key={p.id} canvas={{ w: GAZ_W, h: GAZ_H, bg: p.bg, layers: p.layers }} scale={1}
          stageRef={(el) => { exportRefs.current[p.id] = el; }} />
      ))}
    </div>
  );
}

function TextProps({ l, upd }) {
  return (
    <div className="space-y-3">
      <Textarea value={l.text} onChange={(e) => upd({ text: e.target.value })} rows={2} className="text-sm" data-testid="gz-text-content" />
      <select value={l.font} onChange={(e) => upd({ font: e.target.value })} className="w-full h-9 rounded border px-2 text-sm bg-white" data-testid="gz-text-font">
        {FONTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}</select>
      <div><Label className="text-xs">Taille · {l.size}px</Label><Slider value={[l.size]} min={8} max={80} step={1} onValueChange={([v]) => upd({ size: v })} className="mt-2" /></div>
      <Color label="Couleur" value={l.color} onChange={(c) => upd({ color: c })} testid="gz-text-color" />
      <div className="flex gap-1">
        <Tg active={l.weight >= 600} onClick={() => upd({ weight: l.weight >= 600 ? 400 : 700 })} icon={Bold} />
        <Tg active={l.italic} onClick={() => upd({ italic: !l.italic })} icon={Italic} />
        <div className="w-px bg-border mx-1" />
        <Tg active={l.align === "left"} onClick={() => upd({ align: "left" })} icon={AlignLeft} />
        <Tg active={l.align === "center"} onClick={() => upd({ align: "center" })} icon={AlignCenter} />
        <Tg active={l.align === "right"} onClick={() => upd({ align: "right" })} icon={AlignRight} />
      </div>
    </div>
  );
}
function Tool({ icon: Icon, label, onClick, testid }) {
  return <button onClick={onClick} data-testid={testid} className="flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs hover:bg-accent"><Icon className="w-4 h-4" /> {label}</button>;
}
function Ico({ icon: Icon, onClick, danger, testid }) {
  return <button onClick={onClick} data-testid={testid} className={`w-7 h-7 rounded-md border flex items-center justify-center hover:bg-accent ${danger ? "hover:text-destructive" : ""}`}><Icon className="w-3.5 h-3.5" /></button>;
}
function Tg({ active, onClick, icon: Icon }) {
  return <button onClick={onClick} className={`w-8 h-8 rounded-md border flex items-center justify-center ${active ? "bg-stone-900 text-white" : "bg-white"}`}><Icon className="w-3.5 h-3.5" /></button>;
}
function Color({ label, value, onChange, testid }) {
  return <div className="flex items-center justify-between"><Label className="text-xs">{label}</Label>
    <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-10 h-7 rounded border cursor-pointer" /></div>;
}
